import type { LedgerSafePrimaryScopeKind } from '../api/ledgerSafePostgresPrimaryAPI'

export interface LedgerSafePrimaryReadScope {
  kind: LedgerSafePrimaryScopeKind
  id: string
}

export interface LedgerSafePrimaryReadAdapter {
  ensureRecord: (request: {
    databaseName: string
    storeName: string
    key: IDBValidKey
  }) => Promise<void>
  ensureStore: (request: {
    databaseName: string
    storeName: string
  }) => Promise<void>
  ensureScope: (request: {
    databaseName: string
    storeName: string
    indexName: string
    indexKeyPath: string | string[] | null
    indexValue: unknown
    scope: LedgerSafePrimaryReadScope
  }) => Promise<void>
  ensureWitnessMetadata: () => Promise<void>
  readRecentScope: (request: {
    databaseName: string
    storeName: string
    scope: LedgerSafePrimaryReadScope
    limit: number
    maxBytes: number
    snapshotScopeId?: string
  }) => Promise<unknown[]>
}

const SCOPE_FIELDS: Record<string, LedgerSafePrimaryScopeKind> = {
  book_id: 'book',
  bookId: 'book',
  chapter_id: 'chapter',
  chapterId: 'chapter',
  witness_id: 'witness',
  witnessId: 'witness',
  work_id: 'work',
  workId: 'work',
  source_object_id: 'source_object',
  sourceObjectId: 'source_object',
  context_id: 'context',
  contextId: 'context',
}

const SCOPE_PRIORITY: LedgerSafePrimaryScopeKind[] = [
  'chapter',
  'witness',
  'book',
  'work',
  'source_object',
  'context',
]

let activeAdapter: LedgerSafePrimaryReadAdapter | null = null

export const installLedgerSafePostgresPrimaryReadAdapter = (
  adapter: LedgerSafePrimaryReadAdapter,
): (() => void) => {
  activeAdapter = adapter
  return () => {
    if (activeAdapter === adapter) activeAdapter = null
  }
}

export const resolveLedgerSafePrimaryReadScope = (
  keyPath: string | string[] | null,
  value: unknown,
): LedgerSafePrimaryReadScope | null => {
  const paths = Array.isArray(keyPath) ? keyPath : [keyPath]
  const values = Array.isArray(value) ? value : [value]
  const candidates: LedgerSafePrimaryReadScope[] = []
  paths.forEach((path, index) => {
    if (typeof path !== 'string') return
    const kind = SCOPE_FIELDS[path]
    const id = values[index]
    if (kind && typeof id === 'string' && id) {
      candidates.push({ kind, id })
    }
  })
  return SCOPE_PRIORITY
    .map(kind => candidates.find(candidate => candidate.kind === kind))
    .find((candidate): candidate is LedgerSafePrimaryReadScope =>
      Boolean(candidate)) || null
}

export const ensureLedgerSafePostgresPrimaryRecordCached = async (
  request: Parameters<LedgerSafePrimaryReadAdapter['ensureRecord']>[0],
): Promise<void> => activeAdapter?.ensureRecord(request)

export const ensureLedgerSafePostgresPrimaryStoreCached = async (
  request: Parameters<LedgerSafePrimaryReadAdapter['ensureStore']>[0],
): Promise<void> => activeAdapter?.ensureStore(request)

export const ensureLedgerSafePostgresPrimaryIndexCached = async (
  request: Omit<
    Parameters<LedgerSafePrimaryReadAdapter['ensureScope']>[0],
    'scope'
  >,
): Promise<void> => {
  if (!activeAdapter) return
  const scope = resolveLedgerSafePrimaryReadScope(
    request.indexKeyPath,
    request.indexValue,
  )
  if (!scope) {
    return activeAdapter.ensureStore({
      databaseName: request.databaseName,
      storeName: request.storeName,
    })
  }
  return activeAdapter.ensureScope({ ...request, scope })
}

export const ensureLedgerSafePostgresPrimaryWitnessMetadataCached =
  async (): Promise<void> => activeAdapter?.ensureWitnessMetadata()

export const readLedgerSafePostgresPrimaryRecentScope = async (request: {
  databaseName: string
  storeName: string
  scope: LedgerSafePrimaryReadScope
  limit: number
  maxBytes: number
  snapshotScopeId?: string
}): Promise<unknown[] | null> => activeAdapter
  ? activeAdapter.readRecentScope(request)
  : null

export const resetLedgerSafePostgresPrimaryReadBridgeForTests = (): void => {
  activeAdapter = null
}
